/**
 * Chi è l'utente collegato, per tutta l'applicazione.
 *
 * All'avvio si chiede al backend "chi sono": se il cookie di sessione è
 * ancora valido si entra direttamente, altrimenti si va al login. Il
 * frontend non conserva niente in `localStorage` — la sessione è il cookie
 * httpOnly, che JavaScript non può leggere, ed è il motivo per cui un XSS non
 * può rubarla.
 *
 * Questa risposta però invecchia: il nome e l'email dell'utente si possono
 * cambiare dalla pagina Utenti, e fino a ieri l'intestazione continuava a
 * mostrare i vecchi finché non si ricaricava il browser. Per questo l'utente
 * viene **riletto dal backend** in tre occasioni: quando si torna sulla
 * finestra, quando si cambia pagina, e quando una schermata dichiara di aver
 * modificato proprio l'utente collegato (`ricarica()`).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

import { alDecaderedellaSessione, ErroreApi } from '../api/client.ts';
import { autenticazione } from '../api/scadenziario.ts';
import type { Utente } from '../api/tipi-dati.ts';

interface Contesto {
  utente: Utente | null;
  /** `true` finché non si sa se c'è una sessione valida. */
  inVerifica: boolean;
  accedi(email: string, password: string): Promise<void>;
  esci(): Promise<void>;
  /**
   * Rilegge dal backend l'utente collegato.
   *
   * Da chiamare dopo aver modificato **sé stessi**: la rivalidazione
   * automatica arriverebbe comunque, ma non subito, e nel frattempo la
   * schermata mostrerebbe i dati che l'utente ha appena finito di correggere.
   * Non fallisce mai in modo visibile — vedi il commento dentro.
   */
  ricarica(): Promise<void>;
}

const ContestoAutenticazione = createContext<Contesto | null>(null);

export function ProvvederAutenticazione({ children }: { children: ReactNode }) {
  const [utente, setUtente] = useState<Utente | null>(null);
  const [inVerifica, setInVerifica] = useState(true);

  // All'avvio: c'è già una sessione valida?
  useEffect(() => {
    let annullato = false;

    autenticazione
      .io()
      .then((u) => { if (!annullato) setUtente(u); })
      .catch((errore: unknown) => {
        // Un 401 qui è la normalità, non un problema: significa solo che non
        // si è ancora entrati. Qualunque altro errore va invece mostrato.
        if (!(errore instanceof ErroreApi && errore.nonAutenticato)) {
          console.error('Verifica della sessione fallita', errore);
        }
      })
      .finally(() => { if (!annullato) setInVerifica(false); });

    return () => { annullato = true; };
  }, []);

  // Se una chiamata qualsiasi riceve 401, la sessione è caduta: si torna al
  // login senza aspettare che l'utente scopra da solo che nulla funziona più.
  useEffect(() => alDecaderedellaSessione(() => setUtente(null)), []);

  /**
   * Una rilettura per volta.
   *
   * Tornare sulla finestra e cliccare subito una voce di menu fa scattare due
   * rivalidazioni a distanza di un istante; e in sviluppo `StrictMode` monta
   * ogni componente due volte, quindi anche gli effetti qui sotto partono in
   * coppia. Il controllo è sincrono — sta prima del primo `await` — perciò la
   * seconda chiamata trova già `true` e rinuncia.
   */
  const inRilettura = useRef(false);

  const ricarica = useCallback(async () => {
    if (inRilettura.current) return;
    inRilettura.current = true;

    try {
      setUtente(await autenticazione.io());
    } catch (errore: unknown) {
      /*
       * Silenziosa per scelta. Qui nessuno ha chiesto niente: la rilettura
       * parte da sola, e un guasto passeggero — rete che va e viene, backend
       * che si sta riavviando — non deve svuotare la schermata che l'utente
       * sta guardando. Si tiene quello che c'è e si riproverà al prossimo
       * giro. Il 401 non va gestito nemmeno lui: `alDecaderedellaSessione` è
       * già scattato dentro al client e ha riportato al login.
       */
      if (!(errore instanceof ErroreApi && errore.nonAutenticato)) {
        console.error('Rilettura dell\'utente collegato fallita', errore);
      }
    } finally {
      inRilettura.current = false;
    }
  }, []);

  /*
   * Copia di lavoro di `utente` per la rivalidazione automatica.
   *
   * Gli effetti qui sotto non possono dipendere dallo stato `utente`: `io()`
   * restituisce ogni volta un oggetto nuovo, quindi l'effetto del cambio di
   * rotta — che rivalida appena parte — si riavvierebbe a ogni rilettura e si
   * richiamerebbe all'infinito. Il ref invece cambia senza rimontare niente.
   */
  const utenteCorrente = useRef<Utente | null>(null);
  useEffect(() => { utenteCorrente.current = utente; }, [utente]);

  /** Rivalida solo se c'è qualcuno di collegato: sul login non c'è nulla da rileggere. */
  const rivalida = useCallback(() => {
    if (utenteCorrente.current !== null) void ricarica();
  }, [ricarica]);

  // Ritorno sulla finestra. I due eventi si sovrappongono spesso (cambiando
  // scheda scattano tutti e due), ma la rilettura è già a prova di doppione.
  useEffect(() => {
    const alRitorno = () => {
      // `visibilitychange` scatta anche quando la scheda viene *nascosta*: in
      // quel momento non c'è nessuno che guardi il risultato.
      if (document.visibilityState !== 'visible') return;
      rivalida();
    };

    window.addEventListener('focus', alRitorno);
    document.addEventListener('visibilitychange', alRitorno);

    return () => {
      window.removeEventListener('focus', alRitorno);
      document.removeEventListener('visibilitychange', alRitorno);
    };
  }, [rivalida]);

  // Cambio di rotta: copre chi resta nella stessa finestra e si sposta fra le
  // pagine, che è poi il caso di chi cambia la propria email in «Utenti» e va
  // in «Impostazioni» a mandarsi la prova.
  const { pathname } = useLocation();
  useEffect(() => { rivalida(); }, [pathname, rivalida]);

  const accedi = useCallback(async (email: string, password: string) => {
    setUtente(await autenticazione.accedi(email, password));
  }, []);

  const esci = useCallback(async () => {
    try {
      await autenticazione.esci();
    } finally {
      // Anche se la chiamata fallisce, l'utente ha chiesto di uscire: lo si
      // porta comunque fuori. Il cookie scadrà per conto suo.
      setUtente(null);
    }
  }, []);

  const valore = useMemo(
    () => ({ utente, inVerifica, accedi, esci, ricarica }),
    [utente, inVerifica, accedi, esci, ricarica],
  );

  return (
    <ContestoAutenticazione.Provider value={valore}>{children}</ContestoAutenticazione.Provider>
  );
}

export function useAutenticazione(): Contesto {
  const contesto = useContext(ContestoAutenticazione);
  if (contesto === null) {
    throw new Error('useAutenticazione va usato dentro <ProvvederAutenticazione>');
  }
  return contesto;
}
